import { logger } from '@magicschool/logger';
import type { Tables } from '@magicschool/supabase/types';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { client, queryFactory } from 'hooks/useQuery';
import { validate as isValidUUID } from 'uuid';
import type { InstitutionSearchResult } from '@/app/(teacher)/profile/_actions/getInstitutionsWithinRadius';
import type { TeacherProfileWithRelations } from '@/app/(teacher)/profile/_actions/types';
import {
  updateTeacherLocationInstitution,
  updateTeacherProfileBestPracticesCompleted,
  updateUserTeacherProfile,
} from '@/app/(teacher)/profile/_actions/updateTeacherProfile';
import {
  computeChangedProfileFields,
  OnboardingStepDirection,
  type ProfileFieldsSnapshot,
  ProfileUpdateSurface,
  resolveNamesFromIds,
  trackOnboardingStepInteraction,
  trackProfileUpdated,
} from '@/features/profile/analytics/profileUpdateAnalytics';
import { GradesQuery } from '@/hooks/useGrades';
import { SubjectsQuery } from '@/hooks/useSubjects';
import { TeacherProfileQuery } from '@/hooks/useTeacherProfile';
import { updateAnalyticsUserProperties } from '@/util/amplitude/analyticsClient';
import type { PlaceResult } from '../LocationAutocompleteInput/types';
import { PROFILE_BUILDER_STEPS, type StepVariant } from './constants';
import { stepDataMap } from './stepDataMap';
import teacherProfileToDB from './teacherProfileToDB';
import teacherProfileToStore from './teacherProfileToStore';
import type { TeacherProfileData } from './types';

export interface TeacherProfileStore extends Omit<TeacherProfileData, 'selectedInstitutions'> {
  isProfileBuilderOpen: boolean;
  currentStepId: StepVariant;
  saving: boolean;
  selectedLocation?: PlaceResult;
  selectedProfession?: Tables<'professions'>;
  selectedInstitutions: InstitutionSearchResult[];
  selectedSubjects: string[];
  customSubject?: string;
  newEmail: string;
  plan: number;
  showUpdateProfile: boolean;
  getFormattedInstitutions: () => Array<{ institution_id: string } | { custom_name: string }>;
  setField: SetField<TeacherProfileStore>;
  // Allows selectedInstitutionIds/selectedInstitutions to move together,
  // rather than setting them via two separate setField calls.
  setInstitutions: (ids: string[], institutions: InstitutionSearchResult[]) => void;
  hydrate: (args: {
    teacherProfile: TeacherProfileWithRelations | null | undefined;
    showUpdateProfile?: boolean;
    hideCustomInstitutions?: boolean;
  }) => void;
  updateProfile: ({ surface }: { surface: ProfileUpdateSurface }) => Promise<void>;
  updateLocationInstitution: () => Promise<void>;
  next: () => Promise<void>;
  back: () => void;
  acknowledgeBestPractices: () => Promise<void>;
  handleLocationChange: (place?: PlaceResult) => void;
}

const defaultState = {
  isProfileBuilderOpen: false,
  currentStepId: PROFILE_BUILDER_STEPS.WELCOME,
  selectedGrades: [],
  selectedSubjects: [],
  selectedInstitutions: [],
  selectedInstitutionIds: [],
  fullName: '',
  studentFriendlyName: '',
  profileComplete: false,
  saving: false,
  newEmail: '',
  plan: 1,
  showUpdateProfile: false,
};

const toProfileFieldsSnapshot = (store: TeacherProfileStore): ProfileFieldsSnapshot => ({
  selectedProfessionId: store.selectedProfessionId,
  institutionIds: store.selectedInstitutionIds,
  selectedGrades: store.selectedGrades,
  selectedSubjects: store.selectedSubjects,
  city: store.city,
  stateProvince: store.stateProvince,
  countryCode: store.countryCode,
  fullName: store.fullName,
  studentFriendlyName: store.studentFriendlyName,
});

export const createTeacherProfileStoreSlice = createStoreSlice(
  'TeacherProfileStoreData',
  defaultState,
  ({ set, get, getFull, setField }) => {
    // Baseline for computing Profile Updated's fields_changed — captured once per store instance,
    // re-captured on hydrate() and again immediately after every successful save (see updateProfile).
    let pristineSnapshot: ProfileFieldsSnapshot = {
      selectedProfessionId: undefined,
      institutionIds: [],
      selectedGrades: [],
      selectedSubjects: [],
      city: undefined,
      stateProvince: undefined,
      countryCode: undefined,
      fullName: '',
      studentFriendlyName: '',
    };

    return {
      setField,
      setInstitutions: (ids, institutions) => set({ selectedInstitutionIds: ids, selectedInstitutions: institutions }),
      getFormattedInstitutions: () => {
        const store = get();
        return (store.selectedInstitutionIds ?? [])
          .filter((institutionId) => institutionId?.length)
          .map((institutionId) => {
            if (isValidUUID(institutionId)) {
              return { institution_id: institutionId };
            }
            return { custom_name: institutionId };
          });
      },
      hydrate: ({ teacherProfile, showUpdateProfile }) => {
        // Hydrating part of this store from user data
        const { name, profile_image, student_friendly_name, best_practices_completed, email, plan } = getFull().AuthStoreData.userData;

        set({
          plan,
          newEmail: email ?? '',
          fullName: name ?? '',
          studentFriendlyName: student_friendly_name ?? '',
          profileImage: profile_image ?? '',
          saving: false,
          showUpdateProfile: Boolean(showUpdateProfile),
          isProfileBuilderOpen: showUpdateProfile || !best_practices_completed,
          ...(teacherProfile ? teacherProfileToStore(teacherProfile) : {}), // Data backfilling
        });
        pristineSnapshot = toProfileFieldsSnapshot(get());
      },
      next: async () => {
        const store = get();
        const { currentStepId, selectedProfession, countryCode, updateProfile, plan, profileComplete } = store;

        if (currentStepId !== PROFILE_BUILDER_STEPS.WELCOME) {
          await updateProfile({ surface: ProfileUpdateSurface.Builder });
        }

        trackOnboardingStepInteraction({
          step: currentStepId,
          direction: OnboardingStepDirection.Next,
          hasPriorCompletedProfile: profileComplete,
        });

        // advance to the next step
        const nextStep = stepDataMap[currentStepId].getNextStep({ selectedProfession, countryCode, planId: plan });
        set({ currentStepId: nextStep, saving: false });
      },
      back: () => {
        const { currentStepId, selectedProfession, countryCode, plan, profileComplete } = get();

        const prevStep = stepDataMap[currentStepId].getPreviousStep({ selectedProfession, countryCode, planId: plan });
        trackOnboardingStepInteraction({
          step: currentStepId,
          direction: OnboardingStepDirection.Back,
          hasPriorCompletedProfile: profileComplete,
        });
        set({ currentStepId: prevStep });
      },
      acknowledgeBestPractices: async () => {
        const setAuthField = getFull().AuthStoreData.setField;
        const { profileComplete } = get();

        await updateTeacherProfileBestPracticesCompleted();
        setAuthField('userData')((s) => ({ ...s, best_practices_completed: true }));
        set({ isProfileBuilderOpen: false, profileComplete: true, showUpdateProfile: false });
        trackOnboardingStepInteraction({
          step: PROFILE_BUILDER_STEPS.BEST_PRACTICES,
          direction: OnboardingStepDirection.Next,
          hasPriorCompletedProfile: profileComplete,
        });
        updateAnalyticsUserProperties({
          best_practices_completed: true,
          profile_completed_at: new Date().toISOString(),
        });
      },
      updateProfile: async ({ surface }) => {
        const { userData, setField: setAuthField } = getFull().AuthStoreData;
        const store = get();
        const currentSnapshot = toProfileFieldsSnapshot(store);
        const fieldsChanged = computeChangedProfileFields(pristineSnapshot, currentSnapshot);
        set({ saving: true });
        try {
          const result = await updateUserTeacherProfile(teacherProfileToDB(store));
          if (result.error) {
            // secureServerAction never throws — it always resolves { data, error } — so a failed
            // save must be checked explicitly here, before tracking fires or the fields_changed
            // baseline advances past values that were never actually persisted.
            logger.error('Error updating user teacher profile:', result.error);
            return;
          }

          // fetchQuery (not getQueryData) guarantees a real fetch on a cold cache miss — e.g. a
          // non-US countryCode, which ProfileBuilderHydrated never prefetches (it only warms
          // GradesQuery('US')). `select` is stripped before calling fetchQuery: verified empirically
          // that fetchQuery does NOT apply a query's `select` transform at runtime (it resolves the
          // raw queryFn result), so keeping `select` here would type the result as Grade[]/Subject[]
          // while actually returning the untransformed ServerActionResponse wrapper.
          const { select: _gradesSelect, ...gradesQueryOptions } = GradesQuery(store.countryCode);
          const { select: _subjectsSelect, ...subjectsQueryOptions } = SubjectsQuery;
          const cachedGrades = (await client.fetchQuery(gradesQueryOptions))?.data ?? [];
          const cachedSubjects = (await client.fetchQuery(subjectsQueryOptions))?.data ?? [];
          const subjectIds = store.customSubject ? [store.customSubject, ...store.selectedSubjects] : store.selectedSubjects;

          trackProfileUpdated({
            surface,
            fieldsChanged,
            profession: store.selectedProfession?.name ?? '',
            grades: resolveNamesFromIds(store.selectedGrades, cachedGrades),
            subjects: resolveNamesFromIds(subjectIds, cachedSubjects),
            institutions: resolveNamesFromIds(store.selectedInstitutionIds, store.selectedInstitutions),
            city: store.city,
            stateProvince: store.stateProvince,
            countryCode: store.countryCode,
            fullName: store.fullName,
            studentFriendlyName: store.studentFriendlyName,
            hasPriorCompletedProfile: store.profileComplete,
          });
          pristineSnapshot = currentSnapshot;

          const { studentFriendlyName, fullName, profileImage } = store;
          setAuthField('userData')((s) => ({
            ...s,
            profile_image: profileImage || '',
            student_friendly_name: studentFriendlyName || '',
            name: fullName || '',
          }));
          updateAnalyticsUserProperties({
            name: store.fullName,
            student_friendly_name: store.studentFriendlyName,
            role: store.selectedProfession?.name ?? null,
            subjects: subjectIds,
            grades: store.selectedGrades,
            institutions: store.selectedInstitutions.map((i) => `${i.type}: ${i.name ?? i.id}`),
            city: store.city,
            state: store.stateProvince,
            country: store.countryCode,
          });
        } catch (error) {
          logger.error('Error updating user teacher profile:', error);
        }
        // biome-ignore lint/correctness/useHookAtTopLevel: queryFactory.useUser is a plain query-key builder, not a React hook
        await client.invalidateQueries({ queryKey: queryFactory.useUser(userData.id) });
        await client.invalidateQueries({ queryKey: TeacherProfileQuery.queryKey });
        await client.invalidateQueries({ queryKey: queryFactory.orgUserProfile(userData.id) });
        set({ saving: false });
      },
      updateLocationInstitution: async () => {
        const store = get();
        set({ saving: true });
        try {
          await updateTeacherLocationInstitution({
            city: store.city || null,
            state_province: store.stateProvince || null,
            country_code: store.countryCode || null,
            institutions: store.getFormattedInstitutions(),
          });

          // Invalidate the teacher profile query to refetch fresh data
          await client.invalidateQueries({ queryKey: TeacherProfileQuery.queryKey });
        } catch (error) {
          logger.error('Error updating location and institution:', error);
          throw error; // Re-throw to allow the caller to handle the error
        } finally {
          set({ saving: false });
        }
      },
      handleLocationChange: (place?: PlaceResult) => {
        if (place) {
          const city = place?.addressComponents?.find((component) => component.types.includes('locality'))?.longText;
          const stateProvince = place?.addressComponents?.find((component) =>
            component.types.includes('administrative_area_level_1'),
          )?.longText;
          const countryCode = place?.addressComponents?.find((component) => component.types.includes('country'))?.shortText;
          set({
            selectedLocation: place,
            city: city ?? undefined,
            stateProvince: stateProvince ?? undefined,
            countryCode: countryCode ?? undefined,
          });
        } else {
          set({
            selectedLocation: undefined,
            city: undefined,
            stateProvince: undefined,
            countryCode: undefined,
            selectedInstitutions: [],
            selectedInstitutionIds: [],
          });
        }
      },
    };
  },
);
